// Stage badge palette + filter buckets for the new Admin Leads/Customers views.
// All references rely on the canonical PIPELINE_STAGES from appShared.

import { normalizeStage, stageOrder } from "../appShared";

// Friendly display labels — match the visual style in the reference designs
export const STAGE_BADGE = {
  NEW_INTAKE: { label: "New Intake", bg: "#dbeafe", fg: "#1e40af" },
  DISQUALIFIED: { label: "Disqualified", bg: "#fee2e2", fg: "#b91c1c" },
  NURTURE: { label: "Nurture", bg: "#ede9fe", fg: "#6d28d9" },
  QUALIFIED: { label: "Qualified", bg: "#fef3c7", fg: "#a16207" },
  QUAL_CALL_BOOKED: { label: "Call Booked", bg: "#dbeafe", fg: "#1e40af" },
  NO_SHOW: { label: "No Show", bg: "#fef3c7", fg: "#a16207" },
  QUALIFIED_TO_BUY: { label: "Ready to Buy", bg: "#dbeafe", fg: "#1e40af" },
  FREE_ASSESSMENT_COMPLETE: { label: "Free Assessment Complete", bg: "#e0e7ff", fg: "#4338ca" },
  ASSESSMENT_PURCHASED: { label: "Assessment Purchased", bg: "#ede9fe", fg: "#6d28d9" },
  ASSESSMENT_IN_PROGRESS: { label: "Assessment in Progress", bg: "#ede9fe", fg: "#6d28d9" },
  ASSESSMENT_DELIVERED: { label: "Assessment Delivered", bg: "#dcfce7", fg: "#15803d" },
  SCOPE_CONFIRMED: { label: "Scope Confirmation", bg: "#d1fae5", fg: "#047857" },
  SOW_SENT: { label: "Scope Sent", bg: "#d1fae5", fg: "#047857" },
  SOW_APPROVED: { label: "Scope Approved", bg: "#d1fae5", fg: "#047857" },
  PROJECT_KICKOFF: { label: "Project Kickoff", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_ACTIVE: { label: "Project in Delivery", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_IN_DELIVERY: { label: "Project in Delivery", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_COMPLETED: { label: "Project Complete", bg: "#dcfce7", fg: "#15803d" },
  PROJECT_DELIVERED: { label: "Project Delivered", bg: "#dcfce7", fg: "#15803d" },
  RETAINER_OFFERED: { label: "Retainer Offered", bg: "#fef3c7", fg: "#a16207" },
  RETAINER_ACTIVE: { label: "Retainer", bg: "#dcfce7", fg: "#15803d" },
  AT_RISK: { label: "At Risk", bg: "#fee2e2", fg: "#b91c1c" },
  CHURNED: { label: "Churned", bg: "#f3f4f6", fg: "#475569" },
};

// Customer-facing simplified phase pill
export const CUSTOMER_PHASE_BADGE = {
  ASSESSMENT_PURCHASED: { label: "Assessment", bg: "#ede9fe", fg: "#6d28d9" },
  ASSESSMENT_IN_PROGRESS: { label: "Assessment", bg: "#ede9fe", fg: "#6d28d9" },
  ASSESSMENT_DELIVERED: { label: "Assessment", bg: "#dcfce7", fg: "#15803d" },
  SCOPE_CONFIRMED: { label: "Scope", bg: "#d1fae5", fg: "#047857" },
  SOW_SENT: { label: "Scope", bg: "#d1fae5", fg: "#047857" },
  SOW_APPROVED: { label: "Scope", bg: "#d1fae5", fg: "#047857" },
  PROJECT_KICKOFF: { label: "In Delivery", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_ACTIVE: { label: "In Delivery", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_IN_DELIVERY: { label: "In Delivery", bg: "#0B1628", fg: "#ffffff" },
  PROJECT_COMPLETED: { label: "Complete", bg: "#f3f4f6", fg: "#475569" },
  PROJECT_DELIVERED: { label: "Complete", bg: "#f3f4f6", fg: "#475569" },
  RETAINER_OFFERED: { label: "Retainer Offered", bg: "#fef3c7", fg: "#a16207" },
  RETAINER_ACTIVE: { label: "Retainer", bg: "#dcfce7", fg: "#15803d" },
  AT_RISK: { label: "At Risk", bg: "#fee2e2", fg: "#b91c1c" },
  CHURNED: { label: "Churned", bg: "#f3f4f6", fg: "#475569" },
};

// Customer health pill (computed from project progress / risk)
export const HEALTH_STATES = {
  on_track: { label: "On Track", color: "#16a34a", dot: "#16a34a" },
  needs_review: { label: "Needs Review", color: "#a16207", dot: "#f59e0b" },
  at_risk: { label: "At Risk", color: "#dc2626", dot: "#dc2626" },
  complete: { label: "Complete", color: "#16a34a", dot: "#16a34a" },
};

// Set of stages that classify a prospect as a "Customer" (post-paid-assessment)
export const CUSTOMER_STAGES = new Set([
  "ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED",
  "SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED",
  "PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY",
  "PROJECT_COMPLETED", "PROJECT_DELIVERED",
  "RETAINER_OFFERED", "RETAINER_ACTIVE", "AT_RISK", "CHURNED",
]);

// 6-step journey for the side panel
export const JOURNEY_STEPS = [
  { key: "intake", label: "Intake", at: ["NEW_INTAKE", "DISQUALIFIED", "NURTURE"] },
  { key: "qualified", label: "Qualified", at: ["QUALIFIED", "QUAL_CALL_BOOKED", "NO_SHOW", "QUALIFIED_TO_BUY", "FREE_ASSESSMENT_COMPLETE"] },
  { key: "assess", label: "Assess", at: ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"] },
  { key: "scope", label: "Scope", at: ["SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED"] },
  { key: "project", label: "Project", at: ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY", "PROJECT_COMPLETED", "PROJECT_DELIVERED"] },
  { key: "retainer", label: "Retainer", at: ["RETAINER_OFFERED", "RETAINER_ACTIVE"] },
];

export const journeyIndexFor = (stage) => {
  const norm = normalizeStage(stage);
  for (let i = 0; i < JOURNEY_STEPS.length; i++) {
    if (JOURNEY_STEPS[i].at.includes(norm)) return i;
  }
  return 0;
};

// Prospect helpers
export const isCustomerStage = (stage) => CUSTOMER_STAGES.has(normalizeStage(stage));

// Estimate value for a prospect — uses dynamic pricing if past Assessment_Purchased, otherwise "—"
export const estimateValue = (prospect, pricing) => {
  const stage = normalizeStage(prospect.stage);
  const assessmentPrice = pricing?.pricing?.assessment?.amount || 4995;
  const implPrice = pricing?.pricing?.implementation?.amount || 9995;

  if (stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY) return assessmentPrice + implPrice;
  if (stageOrder[stage] >= stageOrder.ASSESSMENT_PURCHASED) return assessmentPrice;
  return null;
};

export const estimatePaid = (prospect, pricing) => {
  const stage = normalizeStage(prospect.stage);
  const assessmentPrice = pricing?.pricing?.assessment?.amount || 4995;
  const implPrice = pricing?.pricing?.implementation?.amount || 9995;
  let paid = 0;
  if (prospect.payment?.detailed_assessment_paid || stageOrder[stage] >= stageOrder.ASSESSMENT_PURCHASED) {
    paid += assessmentPrice;
  }
  if (prospect.payment?.roadmap_paid || stageOrder[stage] >= stageOrder.PROJECT_IN_DELIVERY) {
    paid += implPrice;
  }
  return paid;
};

// Health classification based on project_health_percent + stage
export const classifyHealth = (prospect) => {
  const stage = normalizeStage(prospect.stage);
  if (stage === "AT_RISK") return "at_risk";
  if (stage === "CHURNED" || stage === "PROJECT_COMPLETED" || stage === "PROJECT_DELIVERED") {
    return prospect.project_health_percent !== undefined && prospect.project_health_percent < 60 ? "at_risk" : "complete";
  }
  if (prospect.project_health_percent !== undefined) {
    if (prospect.project_health_percent < 60) return "at_risk";
    if (prospect.project_health_percent < 80) return "needs_review";
  }
  return "on_track";
};

export const computeAgeDays = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};
